import type { ZoekTaak } from './zoekTypes';

/**
 * Vaste taken per rol voor het zoekvenster: waar het staat, welke knop je
 * klikt en de stappen in gewone taal. `knopTekst` is de zichtbare (Nederlandse)
 * tekst van de knop; "Doe het" klikt hem na het openen van de pagina aan.
 * `alleenAanwijzen` voor knoppen die per rij in een lijst staan, en voor
 * knoppen die iets definitief doen (opslaan, versturen, betalen, publiceren,
 * tekenen, alarm): "Doe het" klikt alleen knoppen die iets openen.
 *
 * Pagina's uit het hoofdmenu komen automatisch uit Layout.tsx; hier staan
 * alleen taken en pagina's die niet (direct) in het hoofdmenu zitten.
 */

const PROFESSIONAL: ZoekTaak[] = [
  {
    id: 'pro:tijdslot',
    titel: 'Tijdslot openzetten in agenda',
    pad: ['Trajecten', 'Agenda'],
    href: '/professional/cockpit?view=agenda',
    knopTekst: 'Inplannen',
    stappen: [
      'Open Trajecten › Agenda',
      'Klik Inplannen (of sleep over een periode)',
      'Kies Beschikbaar tijdslot',
      'Klik Tijd openzetten',
    ],
    trefwoorden: [
      'beschikbaarheid',
      'tijdslot',
      'agenda',
      'inloop',
      'spreekuur',
      'beschikbaar',
    ],
  },
  {
    id: 'pro:sessie',
    titel: 'Sessie of overleg inplannen',
    pad: ['Trajecten', 'Agenda'],
    href: '/professional/cockpit?view=agenda',
    knopTekst: 'Inplannen',
    stappen: [
      'Open Trajecten › Agenda',
      'Klik Inplannen',
      'Kies Medewerker-sessie of Corporate B2B-overleg',
      'Klik Voorstel versturen of Overleg inplannen',
    ],
    trefwoorden: [
      'afspraak maken',
      'afspraak',
      'sessie',
      'overleg',
      'b2b',
      'plannen',
    ],
  },
  {
    id: 'pro:vakantie',
    titel: 'Vakantie of afwezigheid blokkeren',
    pad: ['Trajecten', 'Agenda'],
    href: '/professional/cockpit?view=agenda',
    knopTekst: 'Inplannen',
    stappen: [
      'Open Trajecten › Agenda',
      'Klik Inplannen',
      'Kies Vakantie / afwezigheid',
      'Klik Vastleggen',
    ],
    trefwoorden: [
      'vakantie',
      'vrij',
      'afwezig',
      'prive',
      'blokkeren',
      'blokkade',
    ],
  },
  {
    id: 'pro:ziek',
    titel: 'Ziek melden of calamiteit',
    pad: ['Trajecten', 'Agenda'],
    href: '/professional/cockpit?view=agenda',
    knopTekst: 'Acute calamiteit / ziek melden',
    stappen: [
      'Open Trajecten › Agenda',
      'Klik Acute calamiteit / ziek melden',
      'Klik Agenda 48 uur bevriezen',
    ],
    trefwoorden: [
      'ziek',
      'ziekmelden',
      'calamiteit',
      'noodgeval',
      'agenda bevriezen',
    ],
  },
  {
    id: 'pro:adhoc',
    titel: 'Ad-hoc gesprek registreren',
    pad: ['Trajecten', 'Agenda'],
    href: '/professional/adhoc-melding',
    stappen: [
      'Open Trajecten › Agenda',
      'Klik Ad-hoc melding registreren',
      'Vul het formulier in en sla op',
    ],
    trefwoorden: ['adhoc', 'gesprek zonder afspraak', 'inloop', 'melding'],
  },
  {
    id: 'pro:verslag',
    titel: 'Gespreksverslag schrijven',
    pad: ['Trajecten', 'Lopende trajecten'],
    href: '/professional/cockpit?view=lopende_trajecten',
    stappen: [
      'Open Trajecten › Lopende trajecten',
      'Open het dossier',
      'Klik Nieuw Verslag',
    ],
    trefwoorden: [
      'verslag',
      'gespreksverslag',
      'rapportage',
      'notulen',
      'verslaglegging',
    ],
  },
  {
    id: 'pro:dossier',
    titel: 'Cliëntdossier aanmaken',
    pad: ['Trajecten', 'Lopende trajecten'],
    href: '/professional/cockpit?view=lopende_trajecten',
    knopTekst: 'Nieuw cliënt dossier',
    stappen: [
      'Open Trajecten › Lopende trajecten',
      'Klik Nieuw cliënt dossier (alleen vertrouwenspersonen)',
    ],
    trefwoorden: ['dossier', 'client', 'casus', 'nieuw traject'],
  },
  {
    id: 'pro:uren',
    titel: 'Uren registreren',
    pad: ['Trajecten', 'Financiën'],
    href: '/professional/cockpit?view=financien',
    knopTekst: 'Handmatige uren toevoegen',
    stappen: [
      'Open Trajecten › Financiën',
      'Klik Handmatige uren toevoegen',
      'Vul de uren in en sla op',
    ],
    trefwoorden: [
      'uren',
      'urenstaat',
      'tijd schrijven',
      'declareren',
      'urenregistratie',
    ],
  },
  {
    id: 'pro:factuur',
    titel: 'Factuur aanmaken',
    pad: ['Trajecten', 'Financiën'],
    href: '/professional/cockpit?view=financien',
    knopTekst: 'Nieuwe Factuur Aanmaken',
    stappen: ['Open Trajecten › Financiën', 'Klik Nieuwe Factuur Aanmaken'],
    trefwoorden: [
      'factuur',
      'factureren',
      'rekening',
      'self-billing',
      'facturen',
    ],
  },
  {
    id: 'pro:mijlpaal',
    titel: 'Uren schrijven op mijlpaal',
    pad: ['Trajecten', 'Projecten & mijlpalen'],
    href: '/professional/projecten',
    knopTekst: 'Uren vastleggen',
    alleenAanwijzen: true,
    stappen: [
      'Open Trajecten › Projecten & mijlpalen',
      'Vul uren, datum en omschrijving in bij de mijlpaal',
      'Klik Uren vastleggen',
    ],
    trefwoorden: ['project', 'mijlpaal', 'projecturen'],
  },
  {
    id: 'pro:projectaanvraag',
    titel: 'Projectaanvraag accepteren',
    pad: ['Trajecten', 'Projectvoorstellen'],
    href: '/professional/projectvoorstellen',
    knopTekst: 'Ik accepteer de aanvraag & heb tijd',
    alleenAanwijzen: true,
    stappen: [
      'Open Trajecten › Mijn Projectvoorstellen',
      'Klik Ik accepteer de aanvraag & heb tijd bij de aanvraag',
    ],
    trefwoorden: ['projectvoorstel', 'aanvraag', 'offerte', 'ovo'],
  },
  {
    id: 'pro:opdracht',
    titel: 'Reageren op een opdracht',
    pad: ['Dashboard', 'Opdrachten'],
    href: '/professional/dashboard/opdrachten',
    stappen: [
      'Open Dashboard › Opdrachten',
      'Open een opdracht',
      'Klik Reageren',
    ],
    trefwoorden: ['vacature', 'solliciteren', 'opdracht', 'klus', 'reageren'],
  },
  {
    id: 'pro:screening',
    titel: 'Screeningdocument uploaden',
    pad: ['Mijn Cockpit', 'Screening'],
    href: '/professional/belagom?view=screening',
    stappen: [
      'Open Mijn Cockpit › Screening & Kwalificaties',
      'Klik de uploadknop bij het document (KvK, verzekering, BIG)',
    ],
    trefwoorden: [
      'kvk',
      'verzekering',
      'big',
      'screening',
      'document',
      'uploaden',
    ],
  },
  {
    id: 'pro:stripe',
    titel: 'Bankrekening koppelen (Stripe)',
    pad: ['Mijn Cockpit', 'Uitbetalingen'],
    href: '/professional/belagom?view=uitbetalingen',
    knopTekst: 'Stripe-koppeling starten',
    stappen: [
      'Open Mijn Cockpit › Stripe Uitbetalingen',
      'Klik Stripe-koppeling starten',
    ],
    trefwoorden: [
      'iban',
      'bankrekening',
      'uitbetaling',
      'stripe',
      'betaald worden',
    ],
  },
  {
    id: 'pro:notificaties',
    titel: 'Notificatie-instellingen',
    pad: ['Mijn Cockpit'],
    href: '/professional/belagom?view=notificaties',
    stappen: ['Open Mijn Cockpit › Notificatie-instellingen'],
    trefwoorden: ['meldingen', 'e-mail', 'notificaties', 'sms'],
  },
  {
    id: 'pro:intervisie',
    titel: 'Intervisiegroep aanmelden',
    pad: ['Community', 'Intervisie'],
    href: '/intervisie',
    knopTekst: 'Nieuwe groep aanmelden',
    stappen: ['Open Community › Intervisie', 'Klik Nieuwe groep aanmelden'],
    trefwoorden: ['intervisie', 'peer', 'groep'],
  },
  {
    id: 'pro:forum',
    titel: 'Bericht plaatsen op het forum',
    pad: ['Community', 'Forum'],
    href: '/community',
    knopTekst: 'Nieuw bericht',
    stappen: ['Open Community › Forum', 'Klik Nieuw bericht'],
    trefwoorden: ['forum', 'vraag stellen', 'discussie', 'post'],
  },
  {
    id: 'pro:artikel',
    titel: 'Artikel plaatsen in de newsroom',
    pad: ['Community', 'Newsroom'],
    href: '/newsroom',
    knopTekst: 'Artikel plaatsen',
    stappen: ['Open Community › Newsroom', 'Klik Artikel plaatsen'],
    trefwoorden: ['artikel', 'nieuws', 'blog', 'schrijven'],
  },
  {
    id: 'pro:evenement',
    titel: 'Evenement aanmaken',
    pad: ['Community', 'Connect & Meet'],
    href: '/connect-meet',
    knopTekst: 'Nieuw evenement aanmaken',
    stappen: [
      'Open Community › Connect & Meet',
      'Klik Nieuw evenement aanmaken',
    ],
    trefwoorden: ['evenement', 'event', 'meetup', 'bijeenkomst'],
  },
  {
    id: 'pro:afgesloten',
    titel: 'Afgesloten trajecten',
    pad: ['Trajecten'],
    href: '/professional/cockpit?view=afgesloten_dossiers',
    stappen: ['Open Trajecten', 'Kies Afgesloten trajecten'],
    trefwoorden: ['archief', 'afgesloten', 'oude dossiers'],
  },
  {
    id: 'pro:adhocuren',
    titel: 'Ad-hoc uren',
    pad: ['Trajecten', 'Financiën'],
    href: '/professional/adhoc-uren',
    stappen: ['Open Ad-hoc uren'],
    trefwoorden: ['adhoc', 'uren', 'losse uren'],
  },
  {
    id: 'pro:b2bagenda',
    titel: 'Afspraken met opdrachtgevers',
    pad: ['Trajecten', 'Agenda'],
    href: '/professional/agenda/opdrachtgevers',
    knopTekst: 'Nieuwe afspraak',
    stappen: ['Open de B2B-agenda met opdrachtgevers', 'Klik Nieuwe afspraak'],
    trefwoorden: ['b2b', 'opdrachtgever', 'afspraak', 'overleg'],
  },
  {
    id: 'pro:mijnopleidingen',
    titel: 'Mijn opleidingen',
    pad: ['Academie'],
    href: '/mijn-opleidingen',
    stappen: ['Open Mijn opleidingen'],
    trefwoorden: ['cursus', 'training', 'inschrijvingen', 'academy'],
  },
  {
    id: 'pro:profielfoto',
    titel: 'Profielfoto wijzigen',
    pad: ['Mijn Cockpit', 'Portfolio'],
    href: '/professional/belagom?view=portfolio',
    knopTekst: 'Foto wijzigen',
    stappen: [
      'Open Mijn Cockpit › Zakelijk Portfolio',
      'Klik Foto wijzigen',
      'Kies een JPG, PNG of WebP; de foto wordt direct opgeslagen',
    ],
    trefwoorden: ['avatar', 'pasfoto', 'profielfoto', 'afbeelding', 'foto'],
  },
  {
    id: 'pro:werkervaring',
    titel: 'Werkervaring of opleiding toevoegen',
    pad: ['Mijn Cockpit', 'Portfolio'],
    href: '/professional/belagom?view=portfolio',
    knopTekst: 'Ervaring & Opleiding',
    stappen: [
      'Open Mijn Cockpit › Zakelijk Portfolio',
      'Open het tabblad Ervaring & Opleiding',
      'Klik Toevoegen bij Werkervaring of Opleidingen',
      'Klik Wijzigingen opslaan',
    ],
    trefwoorden: ['cv', 'werkervaring', 'diploma', 'portfolio', 'opleiding'],
  },
  {
    id: 'pro:video',
    titel: 'Introductievideo uploaden',
    pad: ['Mijn Cockpit', 'Portfolio'],
    href: '/professional/belagom?view=portfolio',
    knopTekst: 'Video & Zichtbaarheid',
    stappen: [
      'Open Mijn Cockpit › Zakelijk Portfolio',
      'Open het tabblad Video & Zichtbaarheid',
      'Klik Upload introductievideo en kies het bestand',
    ],
    trefwoorden: ['video', 'pitch', 'introductie', 'filmpje'],
  },
  {
    id: 'pro:opdrachtgeversTonen',
    titel: 'Opdrachtgevers tonen of verbergen op profiel',
    pad: ['Mijn Cockpit', 'Portfolio'],
    href: '/professional/belagom?view=portfolio',
    knopTekst: 'Video & Zichtbaarheid',
    stappen: [
      'Open het tabblad Video & Zichtbaarheid',
      "Zet 'Toon opdrachtgevers op mijn profiel' aan of uit",
      'Klik Wijzigingen opslaan',
    ],
    trefwoorden: ['zichtbaarheid', 'referenties', 'klanten tonen', 'privacy'],
  },
  {
    id: 'pro:factuurgegevens',
    titel: 'Factuurgegevens wijzigen (KvK, btw, IBAN, logo)',
    pad: ['Mijn Cockpit', 'Portfolio'],
    href: '/professional/belagom?view=portfolio',
    knopTekst: 'Factuurgegevens',
    stappen: [
      'Open Mijn Cockpit › Zakelijk Portfolio',
      'Open het tabblad Factuurgegevens',
      'Pas bedrijfsnaam, KvK, btw-nummer, IBAN of logo aan',
      'Klik Factuurgegevens opslaan',
    ],
    trefwoorden: [
      'iban',
      'kvk',
      'btw-nummer',
      'bedrijfslogo',
      'bankrekening',
      'bedrijfsnaam',
    ],
  },
  {
    id: 'pro:btwStatus',
    titel: 'Btw-status (KOR) instellen',
    pad: ['Profiel', 'Facturen'],
    href: '/professional/profile?tab=facturen',
    knopTekst: 'Fiscale status opslaan',
    alleenAanwijzen: true,
    stappen: [
      'Open Profiel › Facturen',
      "Vink 'Ik neem deel aan de KOR' aan of uit",
      'Kies de btw-status per rol',
      'Klik Fiscale status opslaan',
    ],
    trefwoorden: ['btw', 'kor', 'vrijstelling', 'fiscaal', 'belasting'],
  },
  {
    id: 'pro:selfbillingPdf',
    titel: 'Self-billing factuur downloaden',
    pad: ['Profiel', 'Facturen'],
    href: '/professional/profile?tab=facturen',
    knopTekst: 'PDF',
    alleenAanwijzen: true,
    stappen: [
      'Open Profiel › Facturen',
      'Scroll naar Facturatiehistorie',
      'Klik PDF of CSV bij de factuur',
    ],
    trefwoorden: [
      'factuur downloaden',
      'facturatiehistorie',
      'self-billing',
      'pdf',
      'archief',
    ],
  },
  {
    id: 'pro:portfolioPubliceren',
    titel: 'Portfolio publiceren',
    pad: ['Profiel'],
    href: '/professional/profile',
    knopTekst: 'Publiceer mijn portfolio',
    alleenAanwijzen: true,
    stappen: [
      'Open je profiel',
      'Controleer of de checklist op 100% staat',
      'Klik Publiceer mijn portfolio',
    ],
    trefwoorden: [
      'publiceren',
      'live zetten',
      'profiel online',
      'zichtbaar maken',
    ],
  },
  {
    id: 'pro:contractDownloaden',
    titel: 'Contract of documenten downloaden',
    pad: ['Mijn Cockpit', 'Contracten'],
    href: '/professional/belagom?view=contracten',
    knopTekst: 'Downloaden',
    alleenAanwijzen: true,
    stappen: [
      'Open Mijn Cockpit › Contracten & Documenten',
      'Zoek het document (overeenkomst, KvK, polis)',
      'Klik Downloaden',
    ],
    trefwoorden: [
      'contract',
      'overeenkomst',
      'documenten',
      'polis',
      'samenwerkingsovereenkomst',
    ],
  },
  {
    id: 'pro:klacht',
    titel: 'Formele klacht indienen',
    pad: ['Mijn Cockpit', 'Contracten'],
    href: '/professional/belagom?view=contracten',
    stappen: [
      'Open Mijn Cockpit › Contracten & Documenten',
      'Ga naar het klachtenformulier',
      'Vul de klacht in en dien hem in',
    ],
    trefwoorden: ['klacht', 'geschil', 'escalatie', 'klachtenreglement'],
  },
  {
    id: 'pro:tegenvoorstel',
    titel: 'Tegenvoorstel doen op pool-uitnodiging',
    pad: ['Mijn Cockpit', 'Pools & tarieven'],
    href: '/professional/belagom?view=pools',
    knopTekst: 'Tegenvoorstel doen',
    alleenAanwijzen: true,
    stappen: [
      'Open Mijn Cockpit › Mijn Pools & Tarieven',
      'Klik Tegenvoorstel doen bij de uitnodiging',
      'Vul tarief en toelichting in',
      'Klik Tegenvoorstel versturen',
    ],
    trefwoorden: ['tarief', 'onderhandelen', 'bod', 'pool', 'uitnodiging'],
  },
  {
    id: 'pro:poolAccepteren',
    titel: 'Pool-uitnodiging accepteren en Model-OvO tekenen',
    pad: ['Mijn Cockpit', 'Pools & tarieven'],
    href: '/professional/belagom?view=pools',
    knopTekst: 'Direct akkoord',
    alleenAanwijzen: true,
    stappen: [
      'Open Mijn Cockpit › Mijn Pools & Tarieven',
      'Klik Direct akkoord bij de uitnodiging',
      'Klik Onderteken Model-OvO en bevestig met de sms-code',
    ],
    trefwoorden: [
      'pool',
      'model-ovo',
      'ondertekenen',
      'akkoord',
      'uitnodiging',
    ],
  },
  {
    id: 'pro:poolEvaluatie',
    titel: 'Jaarlijkse pool-evaluatie starten',
    pad: ['Mijn Cockpit', 'Pools & tarieven'],
    href: '/professional/belagom?view=pools',
    knopTekst: 'Start jaarlijkse evaluatie',
    alleenAanwijzen: true,
    stappen: [
      'Open Mijn Cockpit › Mijn Pools & Tarieven',
      'Zoek de pool onder Actieve pools',
      'Klik Start jaarlijkse evaluatie',
    ],
    trefwoorden: [
      'indexering',
      'tarief verhogen',
      'evaluatie',
      'pool verlengen',
    ],
  },
  {
    id: 'pro:crisisAanmelden',
    titel: 'Aanmelden voor de crisisdienst',
    pad: ['Mijn Cockpit', 'Crisisdienst'],
    href: '/professional/belagom?view=crisis',
    stappen: [
      'Open Mijn Cockpit › Crisisdienst & Stand-by',
      'Vul BIG/licentie, postcode en mobiel in',
      'Klik Aanmelding opslaan en onderteken met de sms-code',
    ],
    trefwoorden: ['crisis', 'stand-by', 'crisispool', 'responder', 'aanmelden'],
  },
  {
    id: 'pro:standby',
    titel: 'Stand-by dienst claimen of annuleren',
    pad: ['Mijn Cockpit', 'Crisisdienst'],
    href: '/professional/belagom?view=crisis',
    stappen: [
      'Open Mijn Cockpit › Crisisdienst & Stand-by',
      'Open het tabblad Rooster',
      'Klik een vrije shift om te claimen, of je eigen shift om te annuleren',
    ],
    trefwoorden: ['stand-by', 'rooster', 'dienst', 'crisis aan', 'crisis uit'],
  },
  {
    id: 'pro:crisisalarm',
    titel: 'Crisisalarm accepteren en uren indienen',
    pad: ['Mijn Cockpit', 'Crisisdienst'],
    href: '/professional/belagom?view=crisis',
    stappen: [
      'Open Mijn Cockpit › Crisisdienst & Stand-by',
      'Open het tabblad Alarmen',
      'Klik Accepteren & uitrukken',
      'Vul uren en reiskosten in en klik Uren indienen',
    ],
    trefwoorden: ['alarm', 'uitrukken', 'crisis', 'inzet', 'oproep'],
  },
  {
    id: 'pro:waakvlam',
    titel: 'Waakvlam-voorwaarden accepteren',
    pad: ['Mijn Cockpit', 'Waakvlam'],
    href: '/professional/belagom?view=vertrouwenspersoon',
    knopTekst: 'Ik accepteer de Waakvlam-voorwaarden',
    alleenAanwijzen: true,
    stappen: [
      'Open Mijn Cockpit › Waakvlam & Zichtbaarheid',
      'Lees de voorwaarden',
      'Klik Ik accepteer de Waakvlam-voorwaarden',
    ],
    trefwoorden: ['waakvlam', 'paraatheid', 'vertrouwenspersoon', 'sla'],
  },
  {
    id: 'pro:zichtbaarheidsronde',
    titel: 'Zichtbaarheidsronde starten (GPS check-in)',
    pad: ['Mijn Cockpit', 'Waakvlam'],
    href: '/professional/belagom?view=vertrouwenspersoon',
    knopTekst: 'Start Fysieke Zichtbaarheidsronde',
    alleenAanwijzen: true,
    stappen: [
      'Open Mijn Cockpit › Waakvlam & Zichtbaarheid',
      'Kies de vestiging',
      'Klik Start Fysieke Zichtbaarheidsronde',
      'Rond af met Zichtbaarheidsronde Beëindigen',
    ],
    trefwoorden: [
      'check-in',
      'gps',
      'locatie',
      'ronde',
      'aanwezig',
      'koffieronde',
    ],
  },
  {
    id: 'pro:meldingAccepteren',
    titel: 'Nieuwe melding accepteren of afwijzen',
    pad: ['Trajecten', 'Meldingen'],
    href: '/professional/meldingen',
    knopTekst: 'Accepteren',
    alleenAanwijzen: true,
    stappen: [
      'Open Meldingen',
      'Bekijk de melding',
      'Klik Accepteren of Afwijzen en bevestig',
    ],
    trefwoorden: ['melding', 'nieuwe cliënt', 'aanvraag', 'dossier accepteren'],
  },
  {
    id: 'pro:trajectAfsluiten',
    titel: 'Lopend traject afsluiten',
    pad: ['Trajecten', 'Lopende trajecten'],
    href: '/professional/cockpit?view=lopende_trajecten',
    knopTekst: 'Afsluiten',
    alleenAanwijzen: true,
    stappen: [
      'Open Trajecten › Lopende trajecten',
      'Zoek het traject',
      'Klik Afsluiten en bevestig',
    ],
    trefwoorden: ['dossier sluiten', 'traject beëindigen', 'afronden'],
  },
  {
    id: 'pro:dossierHeropenen',
    titel: 'Afgesloten dossier heropenen',
    pad: ['Trajecten', 'Afgesloten trajecten'],
    href: '/professional/cockpit?view=afgesloten_dossiers',
    knopTekst: 'Heropenen',
    alleenAanwijzen: true,
    stappen: [
      'Open Trajecten › Afgesloten trajecten',
      'Zoek het dossier',
      'Klik Heropenen',
    ],
    trefwoorden: ['heropenen', 'dossier terugzetten', 'traject hervatten'],
  },
  {
    id: 'pro:chatOpdrachtgever',
    titel: 'Bericht sturen aan opdrachtgever',
    pad: ['Trajecten', 'Opdrachtgevers & Pools'],
    href: '/professional/cockpit?view=opdrachtgevers',
    knopTekst: 'Chat openen',
    alleenAanwijzen: true,
    stappen: [
      'Open Trajecten › Opdrachtgevers & Pools',
      'Zoek de opdrachtgever',
      'Klik Chat openen en typ je bericht',
    ],
    trefwoorden: ['chat', 'bericht', 'hr', 'opdrachtgever contacteren'],
  },
  {
    id: 'pro:factuurDownloaden',
    titel: 'Factuur downloaden',
    pad: ['Trajecten', 'Financiën'],
    href: '/professional/cockpit?view=financien',
    knopTekst: 'Download',
    alleenAanwijzen: true,
    stappen: ['Open Trajecten › Financiën', 'Zoek de factuur', 'Klik Download'],
    trefwoorden: ['factuur', 'pdf', 'download', 'financien'],
  },
  {
    id: 'pro:urenExporteren',
    titel: 'Urenregistratie exporteren (CSV)',
    pad: ['Trajecten', 'Financiën'],
    href: '/professional/hours',
    knopTekst: 'Exporteren',
    stappen: ['Open Mijn Uren', 'Klik Exporteren; de CSV wordt gedownload'],
    trefwoorden: ['export', 'csv', 'urenstaat', 'download uren'],
  },
  {
    id: 'pro:vervanger',
    titel: 'Vervanger aanmelden bij opdrachtgever',
    pad: ['Trajecten', 'Opdrachtgevers & Pools'],
    href: '/professional/connections',
    knopTekst: 'Vervanger aanmelden',
    alleenAanwijzen: true,
    stappen: [
      'Open Mijn Netwerk',
      'Zoek de actieve samenwerking',
      'Klik Vervanger aanmelden, kies een collega en bevestig',
    ],
    trefwoorden: ['vervanging', 'waarneming', 'collega', 'afwezig'],
  },
  {
    id: 'pro:notitie',
    titel: 'Notitie maken (met herinnering)',
    pad: ['Trajecten', 'Notities'],
    href: '/professional/notes',
    knopTekst: 'Nieuwe Notitie',
    stappen: [
      'Open Notities',
      'Klik Nieuwe Notitie',
      'Kies opdrachtgever, typ de notitie en stel eventueel een herinnering in',
      'Klik Opslaan',
    ],
    trefwoorden: ['notitie', 'aantekening', 'memo', 'herinnering'],
  },
  {
    id: 'pro:todo',
    titel: 'Persoonlijk actiepunt toevoegen',
    pad: ['Dashboard'],
    href: '/professional/dashboard',
    knopTekst: 'Toevoegen',
    alleenAanwijzen: true,
    stappen: [
      'Open het dashboard',
      'Typ je actiepunt in de to-do-balk',
      'Klik Toevoegen (of plan het in op een datum)',
    ],
    trefwoorden: ['to-do', 'todo', 'taak', 'actiepunt', 'herinnering'],
  },
  {
    id: 'pro:jobRadar',
    titel: 'Job-Radar en meldingskanalen instellen',
    pad: ['Dashboard', 'Job-Radar'],
    href: '/professional/dashboard',
    knopTekst: 'Radar & voorkeuren opslaan',
    alleenAanwijzen: true,
    stappen: [
      'Open het dashboard',
      'Zet Radar actief aan en kies disciplines en werkradius',
      'Kies e-mail of sms per kanaal',
      'Klik Radar & voorkeuren opslaan',
    ],
    trefwoorden: [
      'job-radar',
      'alerts',
      'radius',
      'opdrachten volgen',
      'sms',
      'meldingen',
    ],
  },
  {
    id: 'pro:opdrachtgeverBlokkeren',
    titel: 'Opdrachtgever blokkeren (niet meer tonen)',
    pad: ['Dashboard', 'Marktplaats'],
    href: '/professional/dashboard',
    knopTekst: 'Opdrachtgever niet meer tonen',
    alleenAanwijzen: true,
    stappen: [
      'Open het dashboard',
      'Zoek de opdracht in de Marktplaats',
      'Klik het oog-icoon (Opdrachtgever niet meer tonen) en bevestig',
    ],
    trefwoorden: ['blacklist', 'blokkeren', 'verbergen', 'opdrachtgever weren'],
  },
  {
    id: 'pro:blokkadeOpheffen',
    titel: 'Blokkade van opdrachtgever opheffen',
    pad: ['Dashboard', 'Job-Radar'],
    href: '/professional/dashboard',
    knopTekst: 'Opheffen',
    alleenAanwijzen: true,
    stappen: [
      'Open het dashboard',
      'Scroll naar Geblokkeerde opdrachtgevers',
      'Klik Opheffen',
    ],
    trefwoorden: ['deblokkeren', 'blacklist', 'weer tonen'],
  },
  {
    id: 'pro:beroepscertificaat',
    titel: 'Beroepsregistratiecertificaat exporteren',
    pad: ['Data', 'Beroepsregistratie'],
    href: '/professional/data/beroepsregistratie',
    knopTekst: 'Exporteer officieel Beroepsregistratiecertificaat (PDF)',
    stappen: [
      'Open Data › Beroepsregistratie',
      'Kies de periode',
      'Klik Exporteer officieel Beroepsregistratiecertificaat (PDF)',
    ],
    trefwoorden: [
      'pe-punten',
      'certificaat',
      'herregistratie',
      'register',
      'intervisie-uren',
    ],
  },
  {
    id: 'pro:bedrijfscijfers',
    titel: 'Omzet en btw per periode bekijken',
    pad: ['Data', 'Bedrijfscijfers'],
    href: '/professional/data/bedrijfscijfers',
    knopTekst: 'Vergelijk J-o-J',
    stappen: [
      'Open Data › Bedrijfscijfers',
      'Kies jaar, kwartaal of maand en rol',
      'Klik Vergelijk J-o-J',
    ],
    trefwoorden: [
      'omzet',
      'btw-aangifte',
      'cijfers',
      'inkomsten',
      'jaar op jaar',
    ],
  },
  {
    id: 'pro:jaarverslagVerifieren',
    titel: 'Jaarverslagdata controleren en verifiëren',
    pad: ['Data', 'Jaarverslag'],
    href: '/professional/jaarverslag',
    knopTekst: 'Jaarverslag controleren & releasen',
    stappen: [
      'Open Case-registratie & jaarverslag en kies de opdrachtgever',
      'Open het tabblad Jaarverslag controleren & releasen',
      'Corrigeer of sluit uit en klik Mijn data is geverifieerd',
    ],
    trefwoorden: ['audit', 'verificatie', 'case-registratie', 'corrigeren'],
  },
  {
    id: 'pro:jaarverslagReleasen',
    titel: 'Jaarverslag vrijgeven aan opdrachtgever',
    pad: ['Data', 'Jaarverslag'],
    href: '/professional/jaarverslag',
    knopTekst: 'Team',
    stappen: [
      'Open Case-registratie & jaarverslag en kies de opdrachtgever',
      'Open het tabblad Team',
      'Neem eventueel de coördinatorrol op',
      'Klik Integraal jaarverslag valideren & releasen',
    ],
    trefwoorden: ['jaarverslag', 'releasen', 'vrijgeven', 'coordinator'],
  },
  {
    id: 'pro:opdrachtenZoeken',
    titel: 'Opdrachten zoeken in de marktplaats',
    pad: ['Dashboard', 'Opdrachten'],
    href: '/professional/dashboard/opdrachten',
    stappen: [
      'Open Dashboard › Opdrachten',
      'Typ een zoekterm en kies een provincie',
      'Open de opdracht',
    ],
    trefwoorden: ['vacatures', 'opdrachten', 'marktplaats', 'werk zoeken'],
  },
  {
    id: 'pro:opleidingZoeken',
    titel: 'Opleiding of PE-cursus zoeken',
    pad: ['Academie'],
    href: '/opleidingen',
    stappen: [
      'Open de Academie',
      'Kies een hoofdmap of typ een zoekterm',
      'Filter op register, PE-punten, vorm of afstand',
      'Open de opleiding en klik Aanmelden',
    ],
    trefwoorden: ['cursus', 'training', 'pe-punten', 'nascholing', 'academy'],
  },
  {
    id: 'pro:kanaalVolgen',
    titel: 'Communitykanaal volgen',
    pad: ['Community', 'Forum'],
    href: '/community',
    knopTekst: 'Volgen',
    alleenAanwijzen: true,
    stappen: [
      'Open Community › Forum',
      'Zoek het kanaal onder Kanalen',
      'Klik Volgen (Ontvolgen om te stoppen)',
    ],
    trefwoorden: ['kanaal', 'abonneren', 'community', 'volgen'],
  },
  {
    id: 'pro:collegaChat',
    titel: 'Collega zoeken en chat starten',
    pad: ['Community', 'Professionals'],
    href: '/community/professionals',
    knopTekst: 'Chat',
    alleenAanwijzen: true,
    stappen: [
      'Open Community › Professionals',
      'Zoek op naam, beroep of provincie',
      'Klik Chat bij de collega',
    ],
    trefwoorden: [
      'smoelenboek',
      'collega',
      'achterwacht',
      'consultatie',
      'chatten',
    ],
  },
  {
    id: 'pro:groepschat',
    titel: "Groepschat met collega's maken",
    pad: ['Community', 'Chat'],
    href: '/professionals/chat',
    knopTekst: 'Nieuwe groep aanmaken',
    stappen: [
      'Open Chat met Professionals',
      'Klik + (Nieuwe groep aanmaken)',
      "Kies collega's, geef de groep een naam en klik Groep aanmaken",
    ],
    trefwoorden: ['groepschat', 'intervisiegroep', "collega's", 'groep'],
  },
  {
    id: 'pro:berichtBelagom',
    titel: 'Bericht sturen aan BeLagom',
    pad: ['Contact', 'Chat met BeLagom'],
    href: '/berichten?tab=belagom',
    stappen: ['Open Contact › Chat met BeLagom', 'Typ je bericht en verstuur'],
    trefwoorden: ['support', 'helpdesk', 'vraag', 'klantenservice'],
  },
  {
    id: 'pro:bellen',
    titel: 'Opdrachtgever direct bellen',
    pad: ['Contact'],
    href: '/professional/contact',
    stappen: [
      'Open Contact',
      "Kies de organisatie bij 'Direct bellen met opdrachtgever'",
      'Klik Bel en het nummer',
    ],
    trefwoorden: ['bellen', 'telefoon', 'escalatie', 'manager'],
  },
];

const OPDRACHTGEVER: ZoekTaak[] = [
  {
    id: 'og:zoeken',
    titel: 'Professional zoeken',
    pad: ['Dashboard', 'Professional zoeken'],
    href: '/employer/dashboard/zoeken',
    knopTekst: 'Zoeken',
    stappen: [
      'Open Dashboard › Professional zoeken',
      'Kies regio en tier',
      'Klik Zoeken',
    ],
    trefwoorden: [
      'zoeken',
      'coach',
      'expert',
      'kaart',
      'vertrouwenspersoon',
      'professional vinden',
    ],
  },
  {
    id: 'og:vacature',
    titel: 'Vacature plaatsen',
    pad: ['Dashboard', 'Professional zoeken'],
    href: '/opdrachten/nieuw',
    knopTekst: 'Vacature plaatsen',
    alleenAanwijzen: true,
    stappen: [
      'Open Professional zoeken',
      'Klik Nieuwe vacature',
      'Vul de vacature in en klik Vacature plaatsen',
    ],
    trefwoorden: [
      'vacature',
      'opdracht',
      'uitvraag',
      'plaatsen',
      'nieuwe opdracht',
    ],
  },
  {
    id: 'og:byop',
    titel: 'Eigen professional uitnodigen',
    pad: ['Dashboard', 'Eigen professionals'],
    href: '/employer/byop',
    knopTekst: 'Uitnodigen',
    alleenAanwijzen: true,
    stappen: [
      'Open Dashboard › Eigen professionals uitnodigen',
      'Vul de gegevens in',
      'Klik Uitnodigen',
    ],
    trefwoorden: ['byop', 'eigen coach', 'uitnodigen', 'eigen professional'],
  },
  {
    id: 'og:gebruiker',
    titel: 'Collega uitnodigen als gebruiker',
    pad: ['Mijn Cockpit', 'Toegangsbeheer'],
    href: '/employer/mijn-belagom?tab=toegang',
    knopTekst: 'Gebruiker uitnodigen',
    stappen: [
      'Open Mijn Cockpit › Toegangsbeheer',
      'Klik Gebruiker uitnodigen',
      'Klik Uitnodiging versturen',
    ],
    trefwoorden: [
      'collega',
      'teamlid',
      'rechten',
      'gebruiker',
      'toegang',
      'account',
    ],
  },
  {
    id: 'og:medewerkers',
    titel: 'Medewerkers uitnodigen (personeelslijst)',
    pad: ['Medewerkersportaal', 'Activeren'],
    href: '/employer/medewerkersportaal?tab=activeren',
    stappen: [
      'Open Medewerkersportaal › Activeren',
      'Upload de CSV en klik Importeren',
      'Klik Lanceer & Activeer Medewerkerportaal',
    ],
    trefwoorden: [
      'medewerkers',
      'personeel',
      'csv',
      'uitnodigen',
      'portaal',
      'importeren',
    ],
  },
  {
    id: 'og:toegangscode',
    titel: 'Toegangscode voor medewerkers aanmaken',
    pad: ['Medewerkersportaal', 'Toegang'],
    href: '/employer/medewerkersportaal?tab=toegang',
    knopTekst: 'Toegangscode aanmaken',
    stappen: [
      'Open Medewerkersportaal › Toegang',
      'Klik Toegangscode aanmaken',
    ],
    trefwoorden: ['code', 'registratiecode', 'toegang', 'medewerkers'],
  },
  {
    id: 'og:thermometer',
    titel: 'Medewerkersportaal: thermometer',
    pad: ['Medewerkersportaal'],
    href: '/employer/medewerkersportaal?tab=thermometer',
    stappen: ['Open Medewerkersportaal › Thermometer'],
    trefwoorden: ['thermometer', 'stemming', 'welzijn'],
  },
  {
    id: 'og:afspraak',
    titel: 'Afspraak met professional plannen',
    pad: ['Trajecten', 'Agenda'],
    href: '/employer/trajecten/agenda',
    knopTekst: 'Nieuwe afspraak',
    stappen: ['Open Trajecten › Agenda', 'Klik Nieuwe afspraak'],
    trefwoorden: ['afspraak', 'overleg', 'agenda', 'inplannen', 'plannen'],
  },
  {
    id: 'og:fiat',
    titel: 'Uren fiatteren',
    pad: ['Trajecten', 'Agenda'],
    href: '/employer/trajecten/agenda',
    knopTekst: 'Uren fiatteren',
    alleenAanwijzen: true,
    stappen: [
      'Open Trajecten › Agenda',
      'Zoek de uitgevoerde afspraak',
      'Klik Uren fiatteren',
    ],
    trefwoorden: ['uren goedkeuren', 'akkoord', 'fiat', 'goedkeuren'],
  },
  {
    id: 'og:mijlpaal',
    titel: 'Mijlpaal accorderen',
    pad: ['Trajecten', 'Mijlpalen'],
    href: '/employer/trajecten/mijlpalen',
    knopTekst: 'Check & Fiat: accordeer mijlpaal',
    alleenAanwijzen: true,
    stappen: [
      'Open Trajecten › Check & Fiat mijlpalen',
      'Klik Check & Fiat bij de mijlpaal',
    ],
    trefwoorden: ['mijlpaal', 'goedkeuren', 'project', 'fiat', 'accorderen'],
  },
  {
    id: 'og:project',
    titel: 'Organisatieproject starten',
    pad: ['Trajecten', 'Organisatieprojecten'],
    href: '/employer/projecten',
    knopTekst: 'Start Nieuw Organisatie Project',
    stappen: [
      'Open Lopende organisatieprojecten',
      'Klik Start Nieuw Organisatie Project',
    ],
    trefwoorden: ['project', 'workshop', 'teamtraject', 'organisatieproject'],
  },
  {
    id: 'og:facturen',
    titel: 'Facturen bekijken',
    pad: ['Trajecten', 'Facturen'],
    href: '/employer/trajecten/facturen',
    stappen: ['Open Trajecten › Facturen'],
    trefwoorden: ['factuur', 'betalen', 'kosten', 'rekening'],
  },
  {
    id: 'og:verslag',
    titel: 'Gespreksverslag downloaden',
    pad: ['Trajecten', 'Gespreksverslagen'],
    href: '/employer/trajecten/gespreksverslagen',
    knopTekst: 'PDF',
    alleenAanwijzen: true,
    stappen: ['Open Trajecten › Gespreksverslagen', 'Klik PDF bij het verslag'],
    trefwoorden: ['verslag', 'rapport', 'download', 'pdf'],
  },
  {
    id: 'og:pulse',
    titel: 'Pulse-scan starten',
    pad: ['Data', 'Cultuur'],
    href: '/employer/data/cultuur',
    knopTekst: 'Start nieuwe campagne',
    stappen: ['Open Data › Bedrijfsthermometer', 'Klik Start nieuwe campagne'],
    trefwoorden: [
      'pulse',
      'enquete',
      'thermometer',
      'scan',
      'campagne',
      'vragenlijst',
    ],
  },
  {
    id: 'og:offerte',
    titel: 'Incompany-offerte aanvragen',
    pad: ['Academy', 'Opleidingen & workshops'],
    href: '/employer/opleidingen',
    stappen: [
      'Open Academy › Opleidingen & workshops',
      'Open een traject',
      'Klik Start Digitale Offerte-Aanvraag',
      'Klik Offerte-aanvraag indienen',
    ],
    trefwoorden: ['opleiding', 'training', 'offerte', 'incompany', 'cursus'],
  },
  {
    id: 'og:pools',
    titel: 'Pools inrichten',
    pad: ['Mijn Cockpit', 'Pool-Configurator'],
    href: '/employer/mijn-belagom?tab=pools',
    stappen: ['Open Mijn Cockpit › Pool-Configurator'],
    trefwoorden: ['pool', 'menukaart', 'disciplines', 'tarieven'],
  },
  {
    id: 'og:juridisch',
    titel: 'Contracten, documenten & klachten',
    pad: ['Mijn Cockpit', 'Contracten'],
    href: '/employer/mijn-belagom?tab=contracten',
    stappen: ['Open Mijn Cockpit › Contracten & Documenten'],
    trefwoorden: [
      'juridisch',
      'voorwaarden',
      'contract',
      'contracten',
      'documenten',
      'overeenkomst',
      'kvk',
      'support',
      'verwerkersovereenkomst',
      'klacht',
      'klachtenreglement',
    ],
  },
  {
    id: 'og:vacatures',
    titel: 'Mijn vacatures',
    pad: ['Dashboard'],
    href: '/employer/vacancies',
    stappen: ['Open Mijn vacatures'],
    trefwoorden: ['vacatures', 'opdrachten', 'reacties', 'sollicitaties'],
  },
  {
    id: 'og:arbopot',
    titel: 'Arbo-pot',
    pad: ['Data'],
    href: '/employer/arbo-pot',
    stappen: ['Open Arbo-pot'],
    trefwoorden: ['arbo', 'budget', 'pot'],
  },
  {
    id: 'og:profiel',
    titel: 'Organisatieprofiel bewerken',
    pad: ['Mijn Cockpit', 'Organisatieprofiel'],
    href: '/employer/mijn-belagom?tab=profiel',
    knopTekst: 'Profiel bijwerken',
    alleenAanwijzen: true,
    stappen: [
      'Open Mijn Cockpit › Organisatieprofiel',
      'Pas de bedrijfsgegevens aan',
      'Klik Profiel bijwerken',
    ],
    trefwoorden: [
      'bedrijfsgegevens',
      'kvk',
      'adres',
      'contactpersoon',
      'factuurmail',
    ],
  },
  {
    id: 'og:logo',
    titel: 'Logo of bedrijfsfoto uploaden',
    pad: ['Mijn Cockpit', 'Organisatieprofiel'],
    href: '/employer/mijn-belagom?tab=profiel',
    knopTekst: 'Foto uploaden',
    stappen: [
      'Open Mijn Cockpit › Organisatieprofiel',
      'Klik Foto uploaden (of Foto wijzigen)',
      'Kies een JPG of PNG (max 5 MB)',
    ],
    trefwoorden: ['logo', 'foto', 'afbeelding', 'bedrijfslogo'],
  },
  {
    id: 'og:notificaties',
    titel: 'Notificatie-instellingen aanpassen',
    pad: ['Mijn Cockpit', 'Organisatieprofiel'],
    href: '/employer/mijn-belagom?tab=profiel',
    stappen: [
      'Open Mijn Cockpit › Organisatieprofiel',
      'Scroll naar Notificatie-instellingen',
      'Zet e-mail of in-app per melding aan of uit',
    ],
    trefwoorden: ['meldingen', 'e-mail', 'notificaties', 'in-app'],
  },
  {
    id: 'og:vestiging',
    titel: 'Vestiging toevoegen',
    pad: ['Mijn Cockpit', 'Organisatieprofiel'],
    href: '/employer/mijn-belagom?tab=profiel',
    knopTekst: 'Vestiging toevoegen',
    stappen: [
      'Open Mijn Cockpit › Organisatieprofiel',
      'Klik Vestiging toevoegen',
      'Vul naam en postcode in en klik Opslaan',
    ],
    trefwoorden: ['locatie', 'vestiging', 'hoofdvestiging', 'adres'],
  },
  {
    id: 'og:afdeling',
    titel: 'Afdeling toevoegen',
    pad: ['Mijn Cockpit', 'Organisatieprofiel'],
    href: '/employer/mijn-belagom?tab=profiel',
    knopTekst: 'Voeg afdeling toe',
    alleenAanwijzen: true,
    stappen: [
      'Open Mijn Cockpit › Organisatieprofiel',
      'Typ de afdelingsnaam en kies een vestiging',
      'Klik Voeg afdeling toe',
    ],
    trefwoorden: ['afdeling', 'kostenplaats', 'team', 'structuur'],
  },
  {
    id: 'og:werkgebied',
    titel: 'Werkgebied instellen',
    pad: ['Mijn Cockpit', 'Werkgebied'],
    href: '/employer/mijn-belagom?tab=werkgebied',
    knopTekst: 'Werkgebied opslaan',
    alleenAanwijzen: true,
    stappen: [
      'Open Mijn Cockpit › Werkgebied & Radius',
      'Kies een radius of Landelijk',
      'Klik Werkgebied opslaan',
    ],
    trefwoorden: ['radius', 'afstand', 'regio', 'straal', 'landelijk'],
  },
  {
    id: 'og:tegenbod',
    titel: 'Tegenbod van professional accepteren',
    pad: ['Mijn Cockpit', 'Onderhandelingen'],
    href: '/employer/mijn-belagom?tab=onderhandelingen',
    knopTekst: 'Accepteer',
    alleenAanwijzen: true,
    stappen: [
      'Open Mijn Cockpit › Status onderhandelingen',
      'Zoek de rij met een tegenvoorstel',
      'Klik Accepteer, Tegenbod of Wijs af',
    ],
    trefwoorden: [
      'onderhandeling',
      'tarief',
      'tegenvoorstel',
      'prijs',
      'akkoord',
    ],
  },
  {
    id: 'og:herinner',
    titel: 'Uitgenodigde professional herinneren',
    pad: ['Mijn Cockpit', 'Onderhandelingen'],
    href: '/employer/mijn-belagom?tab=onderhandelingen',
    knopTekst: 'Herinner',
    alleenAanwijzen: true,
    stappen: [
      'Open Mijn Cockpit › Status onderhandelingen',
      'Klik Herinner bij de uitnodiging (max. 1× per 24 uur)',
    ],
    trefwoorden: ['herinnering', 'reminder', 'uitnodiging', 'opvolgen'],
  },
  {
    id: 'og:ovo',
    titel: 'Model-OvO tekenen',
    pad: ['Mijn Cockpit', 'Onderhandelingen'],
    href: '/employer/mijn-belagom?tab=onderhandelingen',
    knopTekst: 'Teken Model-OvO',
    alleenAanwijzen: true,
    stappen: [
      'Open Mijn Cockpit › Status onderhandelingen',
      'Klik Teken Model-OvO bij een akkoord',
      'Teken in het venster',
    ],
    trefwoorden: ['contract', 'overeenkomst', 'ovo', 'tekenen', 'ondertekenen'],
  },
  {
    id: 'og:tender',
    titel: 'Open vacature of tender starten na afwijzing',
    pad: ['Mijn Cockpit', 'Onderhandelingen'],
    href: '/employer/mijn-belagom?tab=onderhandelingen',
    knopTekst: 'Start Open Vacature / Tender',
    alleenAanwijzen: true,
    stappen: [
      'Open Mijn Cockpit › Status onderhandelingen',
      'Klik Start Open Vacature / Tender bij een afgewezen rij',
    ],
    trefwoorden: ['tender', 'afgewezen', 'vervanging', 'vacature'],
  },
  {
    id: 'og:payroll',
    titel: 'Personeelsdata (salarisexport) inlezen',
    pad: ['Mijn Cockpit', 'Flexbudget'],
    href: '/employer/mijn-belagom?tab=flexbudget',
    stappen: [
      'Open Mijn Cockpit › L&O-flexbudget',
      'Sleep de HR- of salarisexport in het vak',
      'Wijs de kolommen aan en klik Personeelsdata verwerken',
    ],
    trefwoorden: ['payroll', 'excel', 'salaris', 'afas', 'nmbrs', 'fte'],
  },
  {
    id: 'og:flexbudget',
    titel: 'L&O-flexbudget instellen',
    pad: ['Mijn Cockpit', 'Flexbudget'],
    href: '/employer/mijn-belagom?tab=flexbudget',
    knopTekst: 'Instellingen opslaan',
    alleenAanwijzen: true,
    stappen: [
      'Open Mijn Cockpit › L&O-flexbudget',
      'Kies het budgetmodel en de WKR-cap',
      'Klik Instellingen opslaan',
    ],
    trefwoorden: ['opleidingsbudget', 'wkr', 'academy', 'budget', 'l&o'],
  },
  {
    id: 'og:crisisdienst',
    titel: 'Crisisdienst activeren',
    pad: ['Mijn Cockpit', 'Crisisdienst'],
    href: '/employer/mijn-belagom?tab=crisis',
    knopTekst: 'Start de activatie-wizard',
    stappen: [
      'Open Mijn Cockpit › Crisisdienst & Garantiefonds',
      'Klik Start de activatie-wizard',
      'Doorloop screening, tarief en sms-ondertekening',
    ],
    trefwoorden: ['crisis', '24/7', 'calamiteit', 'noodhulp', 'abonnement'],
  },
  {
    id: 'og:alarm',
    titel: 'Crisisalarm slaan',
    pad: ['Mijn Cockpit', 'Crisisdienst'],
    href: '/employer/mijn-belagom?tab=crisis',
    knopTekst: 'RODE ALARMKNOP',
    alleenAanwijzen: true,
    stappen: [
      'Open Mijn Cockpit › Crisisdienst & Garantiefonds',
      'Klik RODE ALARMKNOP bij de vestiging',
      'Kies het type en klik Alarm versturen',
    ],
    trefwoorden: ['alarm', 'noodgeval', 'incident', 'calamiteit', 'crisishulp'],
  },
  {
    id: 'og:waakvlam',
    titel: 'Vertrouwenspersoon aanstellen (Waakvlam)',
    pad: ['Mijn Cockpit', 'Vertrouwenspersoon'],
    href: '/employer/mijn-belagom?tab=vertrouwenspersoon',
    knopTekst: 'Activeer Waakvlam',
    alleenAanwijzen: true,
    stappen: [
      'Open Mijn Cockpit › Vertrouwenspersoon',
      'Kies de vertrouwenspersoon en vink de voorwaarden aan',
      'Klik Activeer Waakvlam',
    ],
    trefwoorden: ['vertrouwenspersoon', 'waakvlam', 'paraatheid', 'sla'],
  },
  {
    id: 'og:mandaat',
    titel: 'Zichtbaarheidsmandaat vastleggen',
    pad: ['Mijn Cockpit', 'Vertrouwenspersoon'],
    href: '/employer/mijn-belagom?tab=vertrouwenspersoon',
    knopTekst: 'Mandaat vastleggen',
    alleenAanwijzen: true,
    stappen: [
      'Open Mijn Cockpit › Vertrouwenspersoon',
      'Kies vestiging, vertrouwenspersoon, minuten en uurtarief',
      'Klik Mandaat vastleggen',
    ],
    trefwoorden: [
      'zichtbaarheid',
      'spreekuur',
      'koffieronde',
      'mandaat',
      'op locatie',
    ],
  },
  {
    id: 'og:rechten',
    titel: 'Rol of rechten van gebruiker wijzigen',
    pad: ['Mijn Cockpit', 'Gebruikers'],
    href: '/employer/team',
    knopTekst: 'Wijzig rol en plek',
    alleenAanwijzen: true,
    stappen: [
      'Open Mijn Cockpit › Gebruikers',
      'Klik Wijzig rol en plek bij de collega',
      'Klik Opslaan',
    ],
    trefwoorden: [
      'rechten',
      'rol',
      'permissies',
      'toegang intrekken',
      'teamlid',
    ],
  },
  {
    id: 'og:budgetverlenging',
    titel: 'Budgetverlenging goedkeuren',
    pad: ['Dashboard', 'Actiepunten'],
    href: '/employer/dashboard/actiepunten',
    knopTekst: 'Akkoord / Budget vrijgeven',
    alleenAanwijzen: true,
    stappen: [
      'Open Dashboard › Actiepunten',
      'Klik Akkoord / Budget vrijgeven, of Afwijzen',
    ],
    trefwoorden: [
      'actiepunt',
      'extra sessies',
      'verlenging',
      'budget',
      'goedkeuren',
    ],
  },
  {
    id: 'og:ormandaat',
    titel: 'OR-mandaat accorderen',
    pad: ['Dashboard', 'Actiepunten'],
    href: '/employer/dashboard/actiepunten',
    knopTekst: 'OR-mandaat accorderen',
    stappen: [
      'Open Dashboard › Actiepunten',
      'Klik OR-mandaat accorderen',
      'Klik Accorderen',
    ],
    trefwoorden: ['ondernemingsraad', 'wor', 'instemming', 'governance', 'or'],
  },
  {
    id: 'og:signaalkanaal',
    titel: 'Sms- of e-mailmeldingen per signaal instellen',
    pad: ['Dashboard', 'Alle signalen'],
    href: '/employer/dashboard/signalen',
    stappen: [
      'Open Dashboard › Alle signalen',
      'Zet SMS of E-mail aan bij het signaalvenster',
    ],
    trefwoorden: ['signaal', 'sms', 'alert', 'melding', 'waarschuwing'],
  },
  {
    id: 'og:ziekmelding',
    titel: 'Ziekmelding registreren (Poortwachter)',
    pad: ['Trajecten', 'Lopende trajecten'],
    href: '/employer/trajecten/lopend?tab=poortwachter',
    knopTekst: 'Nieuwe ziekmelding geregistreerd',
    stappen: [
      'Open Trajecten › Wet Verbetering Poortwachter',
      'Klik Nieuwe ziekmelding geregistreerd',
      'Doorloop de wizard',
    ],
    trefwoorden: [
      'ziek',
      'verzuim',
      'poortwachter',
      'ziekmelding',
      'uwv',
      'medewerker ziek',
    ],
  },
  {
    id: 'og:b2bafspraak',
    titel: 'B2B-afspraak inplannen',
    pad: ['Trajecten', 'Lopende trajecten'],
    href: '/employer/trajecten/lopend?tab=b2b&b2b=afspraken',
    knopTekst: 'B2B-afspraak',
    stappen: [
      'Open Trajecten › Interne B2B-dienstverlening',
      'Kies Losse B2B-afspraken',
      'Klik B2B-afspraak en vul de afspraak in',
    ],
    trefwoorden: ['b2b', 'dienstverlening', 'service-agenda', 'afspraak'],
  },
  {
    id: 'og:afspraakBevestigen',
    titel: 'Afspraak van professional bevestigen',
    pad: ['Trajecten', 'Agenda'],
    href: '/employer/trajecten/agenda',
    knopTekst: 'Bevestigen',
    alleenAanwijzen: true,
    stappen: [
      'Open Trajecten › Agenda',
      'Klik Bevestigen (of Weigeren) bij het verzoek',
    ],
    trefwoorden: ['bevestigen', 'afspraakverzoek', 'inplanning', 'goedkeuren'],
  },
  {
    id: 'og:afsprakenBeheren',
    titel: 'Afspraken met professional beheren',
    pad: ['Trajecten', 'Gecontracteerde professionals'],
    href: '/employer/trajecten/professionals',
    knopTekst: 'Afspraken beheren',
    alleenAanwijzen: true,
    stappen: [
      'Open Trajecten › Gecontracteerde professionals',
      'Klik Afspraken beheren',
      'Klik Afspraken accepteren',
    ],
    trefwoorden: [
      'afspraken',
      'tarief',
      'samenwerking',
      'contract',
      'voorwaarden',
    ],
  },
  {
    id: 'og:profielProfessional',
    titel: 'Profiel van professional bekijken of downloaden',
    pad: ['Trajecten', 'Gecontracteerde professionals'],
    href: '/employer/trajecten/professionals',
    knopTekst: 'Profiel bekijken',
    alleenAanwijzen: true,
    stappen: [
      'Open Trajecten › Gecontracteerde professionals',
      'Klik Profiel bekijken',
      'Klik Download Profiel (PDF) of Start Gesprek',
    ],
    trefwoorden: ['profiel', 'cv', 'pdf', 'professional', 'gesprek'],
  },
  {
    id: 'og:jaarverslag',
    titel: 'Jaarverslag downloaden',
    pad: ['Trajecten', 'Jaarverslagen'],
    href: '/employer/trajecten/jaarverslagen',
    knopTekst: 'PDF',
    alleenAanwijzen: true,
    stappen: [
      'Open Trajecten › Jaarverslagen',
      'Filter eventueel op jaar',
      'Klik PDF',
    ],
    trefwoorden: ['jaarverslag', 'jaarrapport', 'download', 'pdf'],
  },
  {
    id: 'og:factuurGoedkeuren',
    titel: 'Factuur goedkeuren',
    pad: ['Trajecten', 'Facturen'],
    href: '/employer/trajecten/facturen',
    knopTekst: 'Goedkeuren',
    alleenAanwijzen: true,
    stappen: [
      'Open Trajecten › Facturen',
      'Klik Bekijk PDF ter controle',
      'Klik Goedkeuren',
    ],
    trefwoorden: ['factuur', 'akkoord', 'goedkeuren', 'controle'],
  },
  {
    id: 'og:factuurBetalen',
    titel: 'Factuur betalen',
    pad: ['Trajecten', 'Facturen'],
    href: '/employer/trajecten/facturen',
    knopTekst: 'Nu betalen',
    alleenAanwijzen: true,
    stappen: [
      'Open Trajecten › Facturen',
      'Klik Nu betalen bij de factuur',
    ],
    trefwoorden: ['betalen', 'factuur', 'rekening', 'ideal', 'betaling'],
  },
  {
    id: 'og:salarisadmin',
    titel: 'Factuur naar salarisadministratie sturen',
    pad: ['Trajecten', 'Facturen'],
    href: '/employer/trajecten/facturen',
    knopTekst: 'Naar salarisadministratie',
    alleenAanwijzen: true,
    stappen: [
      'Open Trajecten › Facturen',
      'Klik Naar salarisadministratie bij de factuur',
    ],
    trefwoorden: ['salaris', 'doorsturen', 'boekhouding', 'administratie'],
  },
  {
    id: 'og:mijlpaalFactureren',
    titel: 'Geaccordeerde mijlpalen factureren',
    pad: ['Trajecten', 'Mijlpalen'],
    href: '/employer/trajecten/mijlpalen',
    knopTekst: 'Facturen genereren',
    alleenAanwijzen: true,
    stappen: [
      'Open Trajecten › Check & Fiat mijlpalen',
      'Klik Facturen genereren',
    ],
    trefwoorden: ['factureren', 'mijlpaal', 'facturen maken', 'project'],
  },
  {
    id: 'og:extraUren',
    titel: 'Extra projecturen goedkeuren',
    pad: ['Data', 'Budgetten'],
    href: '/employer/data/budgetten',
    knopTekst: 'Keur extra uren goed',
    alleenAanwijzen: true,
    stappen: [
      'Open Data › Wallet- & Projectbudgetten',
      'Klik Keur extra uren goed bij de mijlpaal',
      'Vul de uren in en klik Bevestig',
    ],
    trefwoorden: ['budget-grendel', 'extra uren', 'overschrijding', 'project'],
  },
  {
    id: 'og:hittekaart',
    titel: 'Hittekaart van pulse-campagne bekijken',
    pad: ['Data', 'Cultuur'],
    href: '/employer/data/cultuur',
    knopTekst: 'Bekijk hittekaart',
    alleenAanwijzen: true,
    stappen: [
      'Open Data › Bedrijfsthermometer',
      'Klik Bekijk hittekaart bij de campagne',
    ],
    trefwoorden: ['hittekaart', 'resultaten', 'pulse', 'stemming'],
  },
  {
    id: 'og:offerteAccepteren',
    titel: 'Incompany-offerte accepteren',
    pad: ['Academy', 'Opleidingen & workshops'],
    href: '/employer/opleidingen',
    knopTekst: 'Accepteer Offerte & Plan Data',
    alleenAanwijzen: true,
    stappen: [
      'Open Academy › Opleidingen & workshops',
      'Ga naar je offerte-aanvragen',
      'Klik Accepteer Offerte & Plan Data',
    ],
    trefwoorden: ['offerte', 'akkoord', 'incompany', 'training', 'opleider'],
  },
  {
    id: 'og:codeDeactiveren',
    titel: 'Toegangscode deactiveren',
    pad: ['Medewerkersportaal', 'Toegang'],
    href: '/employer/medewerkersportaal?tab=toegang',
    knopTekst: 'Deactiveren',
    alleenAanwijzen: true,
    stappen: [
      'Open Medewerkersportaal › Toegang',
      'Klik Deactiveren bij de code',
    ],
    trefwoorden: ['code intrekken', 'blokkeren', 'toegangscode', 'stoppen'],
  },
  {
    id: 'og:reacties',
    titel: 'Reacties op vacature bekijken en beantwoorden',
    pad: ['Dashboard', 'Professional zoeken'],
    href: '/employer/dashboard/zoeken',
    knopTekst: 'Reactie-inbox',
    alleenAanwijzen: true,
    stappen: [
      'Open Dashboard › Professional zoeken',
      'Klik Reactie-inbox bij de vacature',
      'Klik Reageren en kies starten, uitnodigen of afwijzen',
    ],
    trefwoorden: [
      'sollicitatie',
      'reactie',
      'kandidaat',
      'sollicitant',
      'uitnodigen',
    ],
  },
  {
    id: 'og:vacaturePubliceren',
    titel: 'Conceptvacature publiceren',
    pad: ['Dashboard', 'Professional zoeken'],
    href: '/employer/dashboard/zoeken',
    knopTekst: 'Publiceren',
    alleenAanwijzen: true,
    stappen: [
      'Open Dashboard › Professional zoeken',
      'Klik Publiceren bij de conceptvacature',
    ],
    trefwoorden: ['publiceren', 'online zetten', 'concept', 'vacature'],
  },
  {
    id: 'og:kaart',
    titel: 'Professional zoeken op de kaart',
    pad: ['Dashboard', 'Professional zoeken'],
    href: '/employer/dashboard/zoeken',
    knopTekst: 'Open de kaart',
    stappen: ['Open Dashboard › Professional zoeken', 'Klik Open de kaart'],
    trefwoorden: ['kaart', 'map', 'in de buurt', 'regio', 'straal'],
  },
  {
    id: 'og:poolbod',
    titel: 'Professionals uit regio uitnodigen met tariefbod',
    pad: ['Mijn Cockpit', 'Pool-uitnodigingen'],
    href: '/employer/pool-uitnodigingen',
    knopTekst: 'Verstuur uitnodiging',
    alleenAanwijzen: true,
    stappen: [
      'Open Pool-uitnodigingen',
      'Vink professionals aan en vul je bod in',
      'Klik Verstuur uitnodiging',
    ],
    trefwoorden: ['pool', 'bod', 'tarief', 'uitnodigen', 'kandidaten'],
  },
  {
    id: 'og:dossier',
    titel: 'Klantdossier aanmaken',
    pad: ['Dashboard', 'Dossiers'],
    href: '/employer/dossiers',
    knopTekst: 'Nieuw Dossier',
    stappen: [
      'Open Klantendossiers',
      'Klik Nieuw Dossier',
      'Klik Dossier Aanmaken',
    ],
    trefwoorden: ['dossier', 'casus', 'klantdossier', 'traject'],
  },
  {
    id: 'og:arboOpwaarderen',
    titel: 'Arbo-pot opwaarderen',
    pad: ['Data', 'Arbo-pot'],
    href: '/employer/arbo-pot',
    knopTekst: 'Naar betalen',
    alleenAanwijzen: true,
    stappen: ['Open Arbo-pot', 'Kies een bedrag', 'Klik Naar betalen (Stripe)'],
    trefwoorden: ['opwaarderen', 'saldo', 'storten', 'stripe', 'wallet'],
  },
  {
    id: 'og:chatProfessional',
    titel: 'Bericht sturen aan professional',
    pad: ['Contact', 'Chat professioneel'],
    href: '/berichten?tab=opdrachtgever',
    stappen: [
      'Open Contact › Chat & bel professioneel',
      'Kies het gesprek',
      'Typ je bericht en verstuur',
    ],
    trefwoorden: ['chat', 'bericht', 'contact', 'professional', 'mailen'],
  },
  {
    id: 'og:chatBelagom',
    titel: 'Chatten met BeLagom',
    pad: ['Contact', 'Chat met BeLagom'],
    href: '/berichten?tab=belagom',
    stappen: [
      'Open Contact › Chat met BeLagom',
      'Kies het gesprek en verstuur je bericht',
    ],
    trefwoorden: ['support', 'helpdesk', 'belagom', 'vraag', 'klantenservice'],
  },
];

const ADMIN: ZoekTaak[] = [
  {
    id: 'adm:screening',
    titel: 'Screening goedkeuren',
    pad: ['Admin', 'Screenings'],
    href: '/admin/screenings',
    knopTekst: 'Goedkeuren',
    alleenAanwijzen: true,
    stappen: [
      'Open Screenings',
      'Klik Goedkeuren (of Afkeuren) bij de screening',
    ],
    trefwoorden: ['screening', 'verificatie', 'keuren', 'afkeuren'],
  },
  {
    id: 'adm:inloggenAls',
    titel: 'Inloggen als gebruiker',
    pad: ['Admin', 'Gebruikersbeheer'],
    href: '/admin?view=users',
    knopTekst: 'Inloggen als',
    alleenAanwijzen: true,
    stappen: [
      'Open Gebruikersbeheer',
      'Zoek de gebruiker',
      'Klik Inloggen als',
    ],
    trefwoorden: ['gebruiker', 'impersonate', 'account', 'meekijken'],
  },
  {
    id: 'adm:zoekopdracht',
    titel: 'Zoekopdracht behandelen',
    pad: ['Admin', 'Zoekopdrachten'],
    href: '/admin/search-requests',
    knopTekst: 'Start Behandeling',
    alleenAanwijzen: true,
    stappen: ['Open Zoekopdrachten', 'Klik Start Behandeling of Vervuld'],
    trefwoorden: ['zoekopdracht', 'matchen', 'aanvraag'],
  },
  {
    id: 'adm:facturen',
    titel: 'Facturen genereren',
    pad: ['Admin', 'Facturen'],
    href: '/admin/invoices',
    knopTekst: 'Genereer Facturen',
    alleenAanwijzen: true,
    stappen: ['Open Facturen', 'Klik Genereer Facturen'],
    trefwoorden: ['factureren', 'maandrun', 'facturatie'],
  },
  {
    id: 'adm:mt940',
    titel: 'Bankafschrift (MT940) uploaden',
    pad: ['Admin', 'Facturen'],
    href: '/admin/invoices',
    knopTekst: 'MT940 uploaden',
    stappen: ['Open Facturen', 'Klik MT940 uploaden'],
    trefwoorden: ['mt940', 'bank', 'betalingen', 'afschrift'],
  },
  {
    id: 'adm:betaald',
    titel: 'Factuur als betaald markeren',
    pad: ['Admin', 'Facturen'],
    href: '/admin/invoices',
    knopTekst: 'Markeer als betaald',
    alleenAanwijzen: true,
    stappen: ['Open Facturen', 'Klik Markeer als betaald bij de factuur'],
    trefwoorden: ['betaald', 'afboeken'],
  },
  {
    id: 'adm:klacht',
    titel: 'Klacht afhandelen',
    pad: ['Admin', 'Platformklachten'],
    href: '/admin/klachten',
    stappen: [
      'Open Platformklachten',
      'Open de klacht',
      'Schrijf de uitspraak en klik Uitspraak versturen en afsluiten',
    ],
    trefwoorden: ['klacht', 'geschil', 'uitspraak', 'klachtenreglement'],
  },
  {
    id: 'adm:audit',
    titel: 'Cursus keuren (Academy-audit)',
    pad: ['Admin', 'Academy-audit'],
    href: '/admin/academy-audit',
    knopTekst: 'Goedkeuren',
    alleenAanwijzen: true,
    stappen: [
      'Open Academy-audit',
      'Klik Goedkeuren of Afkeuren bij de cursus',
    ],
    trefwoorden: ['cursus', 'opleiding', 'audit', 'keuren'],
  },
  {
    id: 'adm:paspoort',
    titel: 'Kwaliteitspaspoort intrekken',
    pad: ['Admin', 'Kwaliteitspaspoorten'],
    href: '/admin/kwaliteitspaspoorten',
    knopTekst: 'Intrekken',
    alleenAanwijzen: true,
    stappen: ['Open Kwaliteitspaspoorten', 'Klik Intrekken bij het paspoort'],
    trefwoorden: ['paspoort', 'certificaat', 'kwaliteit'],
  },
  {
    id: 'adm:vetting',
    titel: 'Vetting-controle draaien',
    pad: ['Admin', 'Continuous Vetting'],
    href: '/admin/compliance',
    knopTekst: 'Nu controleren',
    stappen: ['Open Continuous Vetting', 'Klik Nu controleren'],
    trefwoorden: ['vetting', 'compliance', 'blokkade', 'controle'],
  },
  {
    id: 'adm:sbi',
    titel: 'SBI-code toevoegen',
    pad: ['Admin', 'SBI-codes'],
    href: '/admin/sbi-codes',
    knopTekst: 'Toevoegen',
    alleenAanwijzen: true,
    stappen: ['Open SBI-codes', 'Vul de code in en klik Toevoegen'],
    trefwoorden: ['sbi', 'branche'],
  },
  {
    id: 'adm:subcategorie',
    titel: 'Academy-subcategorie toevoegen',
    pad: ['Admin', 'Academy-subcategorieën'],
    href: '/admin/academy-subcategorieen',
    knopTekst: 'Toevoegen',
    alleenAanwijzen: true,
    stappen: [
      'Open Academy-subcategorieën',
      'Vul de naam in en klik Toevoegen',
    ],
    trefwoorden: ['categorie', 'academy', 'subcategorie'],
  },
  {
    id: 'adm:broadcast',
    titel: 'Broadcast versturen',
    pad: ['Admin', 'Dashboard'],
    href: '/admin',
    stappen: [
      'Open het admin-dashboard',
      'Klik de tegel Broadcast bericht',
      'Klik Verstuur broadcast',
    ],
    trefwoorden: [
      'broadcast',
      'nieuwsbrief',
      'bericht aan iedereen',
      'aankondiging',
    ],
  },
  {
    id: 'adm:evenement',
    titel: 'Community-evenement aanmaken',
    pad: ['Admin', 'Dashboard'],
    href: '/admin',
    stappen: [
      'Open het admin-dashboard',
      'Klik de tegel Community evenementen',
      'Klik Nieuw evenement',
    ],
    trefwoorden: ['event', 'borrel', 'evenement'],
  },
  {
    id: 'adm:klantenservice',
    titel: 'Klantenservicechat beantwoorden',
    pad: ['Admin', 'Dashboard'],
    href: '/admin',
    knopTekst: 'Klantenservice Chat',
    stappen: [
      'Open het admin-dashboard',
      'Klik de tegel Klantenservice Chat',
      'Kies links een gesprek',
      'Typ je antwoord en verstuur',
    ],
    trefwoorden: ['support', 'helpdesk', 'klantvraag', 'chat', 'ticket'],
  },
  {
    id: 'adm:verbeterpunten',
    titel: 'Verbeterpunt beoordelen',
    pad: ['Admin', 'Dashboard'],
    href: '/admin',
    knopTekst: 'Verbeterpunten Platform',
    stappen: [
      'Open het admin-dashboard',
      'Klik de tegel Verbeterpunten Platform',
      'Open een inzending, kies status en schrijf een reactie',
      'Klik Opslaan',
    ],
    trefwoorden: ['feedback', 'suggestie', 'idee', 'verbetering', 'inzending'],
  },
  {
    id: 'adm:gebruikerZoeken',
    titel: 'Gebruiker zoeken',
    pad: ['Admin', 'Gebruikersbeheer'],
    href: '/admin?view=users',
    stappen: [
      'Open Gebruikersbeheer',
      'Typ naam of e-mail in Zoeken',
      'Filter eventueel op verificatiestatus of rol',
    ],
    trefwoorden: ['gebruikers', 'account', 'lid', 'zoeken', 'rol', 'e-mail'],
  },
  {
    id: 'adm:evenementVerwijderen',
    titel: 'Community-evenement verwijderen',
    pad: ['Admin', 'Dashboard'],
    href: '/admin',
    stappen: [
      'Open het admin-dashboard',
      'Klik de tegel Community evenementen',
      'Klik de prullenbak bij het evenement en bevestig',
    ],
    trefwoorden: ['evenement', 'event', 'annuleren', 'verwijderen'],
  },
  {
    id: 'adm:screeningAfkeuren',
    titel: 'Screening afkeuren',
    pad: ['Admin', 'Screenings'],
    href: '/admin/screenings',
    knopTekst: 'Afkeuren',
    alleenAanwijzen: true,
    stappen: [
      'Open Screenings',
      'Klik Afkeuren bij de screening',
      'Vul reden en feedback in',
      'Klik Afwijzen & mail versturen',
    ],
    trefwoorden: ['afwijzen', 'screening', 'weigeren', 'kvk', 'polis'],
  },
  {
    id: 'adm:cursusAfkeuren',
    titel: 'Cursus afkeuren (Academy-audit)',
    pad: ['Admin', 'Academy-audit'],
    href: '/admin/academy-audit',
    knopTekst: 'Afkeuren',
    alleenAanwijzen: true,
    stappen: [
      'Open Academy-audit',
      'Klik Afkeuren bij de cursus',
      'Vul de toelichting in',
      'Klik Afkeuring bevestigen',
    ],
    trefwoorden: ['cursus', 'afwijzen', 'audit', 'compliance', 'opleiding'],
  },
  {
    id: 'adm:holdOpheffen',
    titel: 'Vetting-hold opheffen',
    pad: ['Admin', 'Continuous Vetting'],
    href: '/admin/compliance',
    knopTekst: 'Opheffen',
    alleenAanwijzen: true,
    stappen: [
      'Open Continuous Vetting',
      'Zoek de professional bij Actieve holds',
      'Klik Opheffen, vul de reden in en bevestig',
    ],
    trefwoorden: [
      'hold',
      'blokkade',
      'vrijgeven',
      'compliance',
      'non-compliant',
    ],
  },
  {
    id: 'adm:herbeoordelen',
    titel: 'Professional herbeoordelen',
    pad: ['Admin', 'Continuous Vetting'],
    href: '/admin/compliance',
    knopTekst: 'Herbeoordelen',
    alleenAanwijzen: true,
    stappen: [
      'Open Continuous Vetting',
      'Zoek de hold bij Actieve holds',
      'Klik Herbeoordelen',
    ],
    trefwoorden: ['hercontrole', 'recheck', 'hold', 'vetting', 'opnieuw'],
  },
  {
    id: 'adm:verlenging',
    titel: 'Verlengingsdocument beoordelen',
    pad: ['Admin', 'Continuous Vetting'],
    href: '/admin/compliance',
    knopTekst: 'Goedkeuren',
    alleenAanwijzen: true,
    stappen: [
      'Ga naar Verlengingen ter beoordeling',
      'Klik Document en controleer het',
      'Vul Geldig tot in',
      'Klik Goedkeuren of Afwijzen',
    ],
    trefwoorden: ['verlenging', 'document', 'polis', 'kvk', 'geldigheid'],
  },
  {
    id: 'adm:sepa',
    titel: 'SEPA-uitbetalingsbestand downloaden',
    pad: ['Admin', 'Facturen'],
    href: '/admin/invoices',
    knopTekst: 'SEPA uitbetaling',
    stappen: [
      'Open Facturen',
      'Klik het tabblad SEPA uitbetaling',
      'Kies jaar en maand, klik Laad overzicht',
      'Selecteer uitbetalingen en klik Download SEPA XML',
    ],
    trefwoorden: ['sepa', 'uitbetaling', 'xml', 'bank', 'payout', 'uitbetalen'],
  },
  {
    id: 'adm:factuurstatus',
    titel: 'Factuurstatus handmatig wijzigen',
    pad: ['Admin', 'Facturen'],
    href: '/admin/invoices',
    stappen: [
      'Open Facturen',
      'Zoek de factuur in de tabel',
      'Kies de nieuwe status in de keuzelijst',
    ],
    trefwoorden: [
      'status',
      'concept',
      'verzonden',
      'achterstallig',
      'geannuleerd',
    ],
  },
  {
    id: 'adm:factuurPdf',
    titel: 'Factuur-PDF downloaden',
    pad: ['Admin', 'Facturen'],
    href: '/admin/invoices',
    knopTekst: 'PDF',
    alleenAanwijzen: true,
    stappen: ['Open Facturen', 'Zoek de factuur', 'Klik PDF'],
    trefwoorden: ['pdf', 'factuur', 'download', 'afdrukken'],
  },
  {
    id: 'adm:sbiDeactiveren',
    titel: 'SBI-code deactiveren of verwijderen',
    pad: ['Admin', 'SBI-codes'],
    href: '/admin/sbi-codes',
    knopTekst: 'Deactiveren',
    alleenAanwijzen: true,
    stappen: [
      'Open SBI-codes',
      'Zoek de code in de tabel',
      'Klik Deactiveren of Verwijderen',
    ],
    trefwoorden: ['sbi', 'kvk', 'branchecode', 'uitschakelen', 'verwijderen'],
  },
  {
    id: 'adm:sbiScope',
    titel: 'SBI-code reikwijdte wijzigen',
    pad: ['Admin', 'SBI-codes'],
    href: '/admin/sbi-codes',
    stappen: [
      'Open SBI-codes',
      'Zoek de code',
      'Klik het label Alle types / Alleen platform-poort om te wisselen',
    ],
    trefwoorden: ['scope', 'type', 'poort', 'sbi', 'reikwijdte'],
  },
  {
    id: 'adm:subcategorieBeheren',
    titel: 'Academy-subcategorie deactiveren of herordenen',
    pad: ['Admin', 'Academy-subcategorieën'],
    href: '/admin/academy-subcategorieen',
    knopTekst: 'Deactiveren',
    alleenAanwijzen: true,
    stappen: [
      'Open Academy-subcategorieën',
      'Klik Deactiveren, of de pijltjes Omhoog/Omlaag',
      'Wijzig een naam inline en druk Enter',
    ],
    trefwoorden: ['categorie', 'volgorde', 'catalogus', 'hernoemen'],
  },
  {
    id: 'adm:inlenerExport',
    titel: 'Belastingdienst-export per opdrachtgever',
    pad: ['Admin', 'Belastingdienst'],
    href: '/admin/belastingdienst',
    knopTekst: 'Export per Opdrachtgever',
    stappen: [
      'Open Belastingdienst',
      'Klik het tabblad Export per Opdrachtgever',
      'Klik Download PDF bij de opdrachtgever',
    ],
    trefwoorden: ['inlener', 'export', 'fiscus', 'dba', 'opdrachtgever'],
  },
  {
    id: 'adm:zzpDossier',
    titel: "Compliance-dossier zzp'er openen",
    pad: ['Admin', 'Belastingdienst'],
    href: '/admin/belastingdienst',
    knopTekst: 'Dossier',
    alleenAanwijzen: true,
    stappen: [
      'Open Belastingdienst',
      "Zoek de zzp'er",
      'Klik Dossier en daarna Download compliance PDF',
    ],
    trefwoorden: [
      'zzp',
      'dossier',
      'schijnzelfstandigheid',
      'compliance',
      'pdf',
    ],
  },
  {
    id: 'adm:newsroomModeratie',
    titel: 'Newsroom-artikel of reactie verwijderen',
    pad: ['Community', 'Newsroom'],
    href: '/newsroom',
    stappen: [
      'Open de Newsroom en het artikel',
      'Klik Verwijderen (artikel) of de prullenbak bij een reactie',
      'Bevestig',
    ],
    trefwoorden: ['moderatie', 'nieuws', 'artikel', 'reactie', 'verwijderen'],
  },
  {
    id: 'adm:klachten',
    titel: 'Platformklachten',
    pad: ['Admin'],
    href: '/admin/klachten',
    stappen: ['Open Platformklachten'],
    trefwoorden: ['klachten'],
  },
  {
    id: 'adm:stripe',
    titel: 'Stripe-incidenten',
    pad: ['Admin'],
    href: '/admin/stripe-incidenten',
    stappen: ['Open Stripe-incidenten'],
    trefwoorden: ['stripe', 'betaling', 'incident', 'uitbetaling'],
  },
  {
    id: 'adm:belasting',
    titel: 'Belastingdienst',
    pad: ['Admin'],
    href: '/admin/belastingdienst',
    stappen: ['Open Belastingdienst'],
    trefwoorden: ['belasting', 'dac7', 'fiscaal'],
  },
  {
    id: 'adm:instellingen',
    titel: 'Platforminstellingen',
    pad: ['Admin'],
    href: '/admin/profile',
    stappen: ['Open Platforminstellingen'],
    trefwoorden: ['instellingen', 'sepa', 'configuratie'],
  },
];

const OPLEIDER: ZoekTaak[] = [
  {
    id: 'opl:cursus',
    titel: 'Cursus invoeren',
    pad: ['Academy Management', 'Cursus invoeren'],
    href: '/opleider/academy/cursus-invoeren',
    stappen: [
      'Open Academy Management › Cursus Invoeren',
      'Doorloop de wizard',
      'Klik Opleiding opslaan',
    ],
    trefwoorden: [
      'cursus',
      'training',
      'opleiding toevoegen',
      'aanbod',
      'nieuwe cursus',
    ],
  },
  {
    id: 'opl:publiceren',
    titel: 'Cursus publiceren',
    pad: ['Academy Management', 'Preview'],
    href: '/opleider/academy/preview',
    knopTekst: 'Publiceren',
    alleenAanwijzen: true,
    stappen: ['Open Preview-Zandbak', 'Klik Publiceren'],
    trefwoorden: ['publiceren', 'online zetten', 'live'],
  },
  {
    id: 'opl:presentie',
    titel: 'Presentie bevestigen',
    pad: ['Academy Management', 'Trainingen'],
    href: '/opleider/academy/trainingen',
    stappen: [
      'Open Trainingen Overzicht',
      'Open de presentielijst',
      'Klik Presentie bevestigen',
    ],
    trefwoorden: ['presentie', 'aanwezigheid', 'presentielijst'],
  },
  {
    id: 'opl:groepsbericht',
    titel: 'Groepsbericht aan cursisten',
    pad: ['Academy Management', 'Trainingen'],
    href: '/opleider/academy/trainingen',
    stappen: [
      'Open Trainingen Overzicht',
      'Klik de megafoon (Stuur groepsbericht)',
      'Klik Verstuur naar alle cursisten',
    ],
    trefwoorden: ['broadcast', 'mail cursisten', 'groepsbericht'],
  },
  {
    id: 'opl:team',
    titel: 'Teamlid uitnodigen',
    pad: ['Academy Management', 'Teambeheer'],
    href: '/opleider/academy/team',
    knopTekst: 'Teamlid uitnodigen',
    stappen: [
      'Open Teambeheer',
      'Klik Teamlid uitnodigen',
      'Klik Uitnodiging versturen',
    ],
    trefwoorden: ['trainer', 'collega', 'team', 'uitnodigen'],
  },
  {
    id: 'opl:offerte',
    titel: 'Offerte versturen',
    pad: ['Dashboard', 'Offerte-aanvragen'],
    href: '/opleider/dashboard/offerte-aanvragen',
    knopTekst: 'Offerte versturen',
    alleenAanwijzen: true,
    stappen: [
      'Open B2B Offerte-Aanvragen',
      'Vul de offerte in',
      'Klik Offerte versturen',
    ],
    trefwoorden: ['offerte', 'rfq', 'incompany'],
  },
  {
    id: 'opl:factuur',
    titel: 'Factuur sturen aan cursist',
    pad: ['Dashboard', 'Cursisten'],
    href: '/opleider/dashboard/cursisten',
    knopTekst: 'Factuur sturen',
    alleenAanwijzen: true,
    stappen: [
      'Open Cursist-Gegevens',
      'Klik Factuur sturen',
      'Klik Factuur verzenden',
    ],
    trefwoorden: ['factuur', 'betaling', 'cursist'],
  },
  {
    id: 'opl:stripe',
    titel: 'IBAN koppelen via Stripe',
    pad: ['Finance', 'Stripe'],
    href: '/opleider/finance/stripe',
    knopTekst: 'Koppel IBAN via Stripe',
    stappen: [
      'Open Finance › Stripe Connect Express',
      'Klik Koppel IBAN via Stripe',
    ],
    trefwoorden: ['stripe', 'iban', 'uitbetaling', 'bankrekening'],
  },
  {
    id: 'opl:chat',
    titel: 'Chat met cursist',
    pad: ['Chats', 'Cursisten'],
    href: '/opleider/chats/cursisten',
    stappen: ['Open Chats › Cursist-Gesprekken'],
    trefwoorden: ['chat', 'bericht', 'cursist'],
  },
  {
    id: 'opl:ledger',
    titel: 'Transacties bekijken',
    pad: ['Finance', 'Ledger'],
    href: '/opleider/finance/ledger',
    stappen: ['Open Finance › Transactie Ledger'],
    trefwoorden: ['omzet', 'transacties', 'fees', 'ledger'],
  },
  {
    id: 'opl:signalen',
    titel: 'Signaal in actiecentrum afhandelen',
    pad: ['Dashboard', 'Signalen'],
    href: '/opleider/dashboard',
    knopTekst: 'Alle signalen',
    stappen: [
      'Open het dashboard',
      'Filter op een categorie-tegel',
      'Klik een signaal om het te openen',
    ],
    trefwoorden: ['melding', 'signaal', 'notificatie', 'actiecentrum', 'feed'],
    opleiderRollen: ['OWNER', 'ADMINISTRATOR'],
  },
  {
    id: 'opl:zakelijkChat',
    titel: 'Chatten met werkgever of HR-contact',
    pad: ['Chats', 'Zakelijk overleg'],
    href: '/opleider/chats/zakelijk',
    stappen: [
      'Open Chats › Zakelijk Overleg',
      'Kies het bedrijf in de lijst',
      'Typ en verstuur je bericht',
    ],
    trefwoorden: ['werkgever', 'bedrijf', 'hr', 'opdrachtgever', 'overleg'],
    opleiderRollen: ['OWNER', 'ADMINISTRATOR'],
  },
  {
    id: 'opl:support',
    titel: 'Chatten met BeLagom support',
    pad: ['Chats', 'Cursisten'],
    href: '/opleider/chats/cursisten',
    knopTekst: 'Chat met BeLagom support',
    stappen: [
      'Open Chats › Cursist-Gesprekken',
      'Klik Chat met BeLagom support',
      'Typ je vraag en verstuur',
    ],
    trefwoorden: ['support', 'helpdesk', 'hulp', 'vraag', 'klantenservice'],
  },
  {
    id: 'opl:uitvoerdatum',
    titel: 'Uitvoerdatum incompany-training vastleggen',
    pad: ['Dashboard', 'Offerte-aanvragen'],
    href: '/opleider/dashboard/offerte-aanvragen',
    stappen: [
      'Open B2B Offerte-Aanvragen',
      'Klik het filter Alle',
      'Zoek de geaccepteerde offerte',
      'Kies de datum en klik Datum vastleggen',
    ],
    trefwoorden: ['uitvoerdatum', 'incompany', 'planning', 'trainingsdag'],
    opleiderRollen: ['OWNER', 'ADMINISTRATOR'],
  },
  {
    id: 'opl:offerteBijwerken',
    titel: 'Verzonden offerte bijwerken',
    pad: ['Dashboard', 'Offerte-aanvragen'],
    href: '/opleider/dashboard/offerte-aanvragen',
    knopTekst: 'Offerte bijwerken',
    alleenAanwijzen: true,
    stappen: [
      'Open B2B Offerte-Aanvragen',
      'Zoek de verzonden aanvraag',
      'Pas prijs of toelichting aan',
      'Klik Offerte bijwerken',
    ],
    trefwoorden: ['offerte', 'prijs', 'aanpassen', 'rfq', 'b2b'],
    opleiderRollen: ['OWNER', 'ADMINISTRATOR'],
  },
  {
    id: 'opl:cursisten',
    titel: 'Cursistgegevens per opleiding bekijken',
    pad: ['Dashboard', 'Cursisten'],
    href: '/opleider/dashboard/cursisten',
    stappen: [
      'Open Cursist-Gegevens',
      'Kies de opleiding in de keuzelijst',
      'Bekijk contactgegevens en status',
    ],
    trefwoorden: [
      'aanmeldingen',
      'inschrijvingen',
      'deelnemers',
      'cursisten',
      'registraties',
    ],
    opleiderRollen: ['OWNER', 'ADMINISTRATOR'],
  },
  {
    id: 'opl:cursusBewerken',
    titel: 'Cursus bewerken',
    pad: ['Academy Management', 'Trainingen'],
    href: '/opleider/academy/trainingen',
    stappen: [
      'Open Trainingen Overzicht',
      'Klik het potlood (Bewerken) bij de training',
      'Pas aan en sla op',
    ],
    trefwoorden: ['wijzigen', 'aanpassen', 'training', 'opleiding', 'editen'],
    opleiderRollen: ['OWNER', 'ADMINISTRATOR'],
  },
  {
    id: 'opl:groepschat',
    titel: 'Groepschat met cursisten starten',
    pad: ['Academy Management', 'Trainingen'],
    href: '/opleider/academy/trainingen',
    stappen: [
      'Open Trainingen Overzicht',
      'Klik het groepschat-icoon bij de training',
    ],
    trefwoorden: ['groepschat', 'klas', 'bulkchat', 'deelnemers', 'chat'],
  },
  {
    id: 'opl:depubliceren',
    titel: 'Cursus depubliceren',
    pad: ['Academy Management', 'Preview'],
    href: '/opleider/academy/preview',
    knopTekst: 'Depubliceren',
    stappen: [
      'Open Preview-Zandbak',
      'Kies de training in de keuzelijst',
      'Klik Depubliceren en bevestig',
    ],
    trefwoorden: ['offline', 'verbergen', 'depubliceren', 'uit academy halen'],
    opleiderRollen: ['OWNER', 'ADMINISTRATOR'],
  },
  {
    id: 'opl:voorvertonen',
    titel: 'Cursusweergave voorvertonen',
    pad: ['Academy Management', 'Preview'],
    href: '/opleider/academy/preview',
    knopTekst: 'Detail-pop-up openen',
    stappen: [
      'Open Preview-Zandbak',
      'Kies de training',
      'Klik Detail-pop-up openen',
    ],
    trefwoorden: ['preview', 'voorbeeld', 'weergave', 'zandbak', 'controle'],
    opleiderRollen: ['OWNER', 'ADMINISTRATOR'],
  },
  {
    id: 'opl:cursusVerwijderen',
    titel: 'Cursus verwijderen',
    pad: ['Dashboard', 'Mijn opleidingen'],
    href: '/opleider/dashboard',
    stappen: [
      'Open het dashboard',
      'Scroll naar Mijn opleidingen',
      'Klik de prullenbak bij de opleiding en bevestig',
    ],
    trefwoorden: ['verwijderen', 'wissen', 'opleiding', 'training'],
    opleiderRollen: ['OWNER', 'ADMINISTRATOR'],
  },
  {
    id: 'opl:teamlidWijzigen',
    titel: 'Rol of klassen van teamlid wijzigen',
    pad: ['Academy Management', 'Teambeheer'],
    href: '/opleider/academy/team',
    knopTekst: 'Wijzig',
    alleenAanwijzen: true,
    stappen: [
      'Open Teambeheer',
      'Klik Wijzig bij het teamlid',
      'Kies rol en klassen en klik Opslaan',
    ],
    trefwoorden: ['rol', 'bevoegdheid', 'trainer', 'rechten', 'klassen'],
    opleiderRollen: ['OWNER'],
  },
  {
    id: 'opl:teamlidVerwijderen',
    titel: 'Teamlid verwijderen',
    pad: ['Academy Management', 'Teambeheer'],
    href: '/opleider/academy/team',
    stappen: [
      'Open Teambeheer',
      'Klik de prullenbak bij het teamlid',
      'Bevestig',
    ],
    trefwoorden: ['verwijderen', 'toegang intrekken', 'collega', 'team'],
    opleiderRollen: ['OWNER'],
  },
  {
    id: 'opl:stripeDashboard',
    titel: 'Stripe-dashboard openen',
    pad: ['Finance', 'Stripe'],
    href: '/opleider/finance/stripe',
    knopTekst: 'Stripe-dashboard openen',
    stappen: [
      'Open Finance › Stripe Connect Express',
      'Klik Stripe-dashboard openen',
    ],
    trefwoorden: ['stripe', 'uitbetaling', 'payout', 'saldo', 'bank'],
    opleiderRollen: ['OWNER'],
  },
  {
    id: 'opl:factuurPdf',
    titel: 'Verkoop- of fee-factuur downloaden',
    pad: ['Finance', 'Ledger'],
    href: '/opleider/finance/ledger',
    knopTekst: 'Verkoopfactuur',
    alleenAanwijzen: true,
    stappen: [
      'Open Finance › Transactie Ledger',
      'Filter eventueel op markt',
      'Klik Verkoopfactuur of BeLagom fee-factuur',
    ],
    trefwoorden: ['factuur', 'pdf', 'fee', 'download', 'boekhouding'],
    opleiderRollen: ['OWNER'],
  },
  {
    id: 'opl:feesCsv',
    titel: 'Fee- en btw-overzicht exporteren',
    pad: ['Finance', 'Fee & BTW'],
    href: '/opleider/finance/fees',
    knopTekst: 'Download CSV',
    stappen: ['Open Finance › Fee & BTW Overzicht', 'Klik Download CSV'],
    trefwoorden: ['btw', 'fee', 'csv', 'export', 'administratie'],
    opleiderRollen: ['OWNER'],
  },
  {
    id: 'opl:klacht',
    titel: 'Formele klacht indienen',
    pad: ['Finance', 'Helpdesk & Escalaties'],
    href: '/opleider/finance/helpdesk',
    knopTekst: 'Formele klacht veilig indienen',
    alleenAanwijzen: true,
    stappen: [
      'Open Finance › Helpdesk & Escalaties',
      'Vul contact, categorie en de klacht in',
      'Vink de verklaring aan',
      'Klik Formele klacht veilig indienen',
    ],
    trefwoorden: [
      'klacht',
      'geschil',
      'escalatie',
      'klachtenreglement',
      'ticket',
    ],
    opleiderRollen: ['OWNER', 'ADMINISTRATOR'],
  },
  {
    id: 'opl:analytics',
    titel: 'Analytics bekijken',
    pad: ['Data', 'Analytics'],
    href: '/opleider/data/analytics',
    stappen: ['Open Data › Analytics', 'Kies Maand, Kwartaal of Jaar'],
    trefwoorden: ['statistieken', 'omzet', 'cijfers', 'analytics', 'dashboard'],
    opleiderRollen: ['OWNER', 'ADMINISTRATOR'],
  },
  {
    id: 'opl:reviews',
    titel: 'Reviews bekijken',
    pad: ['Data', 'Reviews'],
    href: '/opleider/data/reviews',
    stappen: ['Open Data › Reviews'],
    trefwoorden: ['reviews', 'beoordelingen', 'scores', 'sterren'],
    opleiderRollen: ['OWNER', 'ADMINISTRATOR'],
  },
  {
    id: 'opl:feedback',
    titel: 'Cursistfeedback lezen',
    pad: ['Data', 'Feedback'],
    href: '/opleider/data/feedback',
    stappen: ['Open Data › Feedback'],
    trefwoorden: ['feedback', 'evaluatie', 'mening', 'cursisten'],
    opleiderRollen: ['OWNER', 'ADMINISTRATOR'],
  },
];

export const takenVoorRol = (
  rol?: string,
  opleiderRol?: string | null
): ZoekTaak[] => {
  switch (rol) {
    case 'professional':
      return PROFESSIONAL;
    case 'opdrachtgever':
    case 'employer_member':
      return OPDRACHTGEVER;
    case 'admin':
      return ADMIN;
    case 'opleider': {
      // Zonder bekende teamrol het minimale TRAINER-menu aanhouden (zoals Layout.tsx)
      const teamrol =
        opleiderRol === 'OWNER' || opleiderRol === 'ADMINISTRATOR'
          ? opleiderRol
          : 'TRAINER';
      return OPLEIDER.filter(
        taak => !taak.opleiderRollen || taak.opleiderRollen.includes(teamrol)
      );
    }
    default:
      return [];
  }
};
